import React, { useMemo, useCallback } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Info, Calculator, Plus, Trash2 } from "lucide-react";
import { useLingui } from "@lingui/react";
import { msg, Trans } from "@lingui/macro";
import {
  formatCHF,
  rappenToCHF,
  chfToRappen,
  DEFAULT_CURRENCY,
} from "@/lib/money";
import { useMultiGroupCostCalculation } from "@/hooks/useMultiGroupCostCalculation";
// `TariffVersions` (groupType -> tariff-version id) is a contract-payload type, not a
// `@/types/billing` export — `@/types/billing` only has the singular `TariffVersion`.
import type { TariffVersions } from "./contractDetailsPayload";
import { useCurrency } from "@/hooks/useCurrency";
import { useEnumOptions } from "@/hooks/settings/useEffectiveEnums";
import { useBillingSettings } from "@/hooks/useBilling";
import type {
  EnhancedMultiGroupCostResult,
  Service,
  ServiceDetail,
} from "@/types/billing";

interface WeekdayDiscount {
  [day: number]: number; // day 1-5 (Mon-Fri), amount in Rappen
}


interface CustomDiscount {
  id?: string;
  name: string;
  type: "percentage" | "amount";
  value: number;
}

interface DiscountsData {
  weekday?: WeekdayDiscount;
  custom?: CustomDiscount[];
}

interface MultiGroupServicePattern {
  [groupType: string]: Record<string, ServiceDetail[]>;
}

interface ContractWizardStepDiscountsProps {
  discounts: DiscountsData;
  onDiscountsChange: (discounts: DiscountsData) => void;
  servicePattern?: MultiGroupServicePattern;
  tariffVersions?: TariffVersions;
  childId?: string;
  services?: Service[];
  subsidised?: boolean;
  subsidyAmount?: number; // in Rappen
  siblingDiscountManual?: boolean;
  onSiblingDiscountManualChange?: (next: boolean) => void;
  startDate?: string;
  showHeader?: boolean;
}

const WEEKDAYS = [
  { key: 1, label: "Monday" },
  { key: 2, label: "Tuesday" },
  { key: 3, label: "Wednesday" },
  { key: 4, label: "Thursday" },
  { key: 5, label: "Friday" },
];

/**
 * Value input for a single custom discount row.
 *
 * An amount discount may be negative (a surcharge that ADDS to the parent
 * total). A controlled `type="number"` input cannot express an in-progress
 * negative value: while typing a lone "-" the browser reports an empty value,
 * so the parent collapses it to 0 and re-renders, discarding the minus before
 * any digit can follow. We keep a local text draft so partial input ("-",
 * "-0.") survives while typing, and only re-sync from props when the value
 * changes from the outside (type switch resets to 0, or existing data loads) —
 * tracked via `lastEmitted` so our own edits never clobber the draft.
 */
function CustomDiscountValueInput({
  id,
  type,
  value,
  currencyLabel,
  onChange,
}: {
  id: string;
  type: "percentage" | "amount";
  value: number; // percentage: 0-100; amount: minor units (Rappen)
  currencyLabel: string;
  onChange: (value: number) => void;
}) {
  // Major-unit value shown in the field (CHF for amounts, percent otherwise).
  const displayValue = type === "percentage" ? value : rappenToCHF(value);
  const [draft, setDraft] = React.useState<string>(() => String(displayValue));
  const lastEmitted = React.useRef<number>(value);

  React.useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setDraft(String(displayValue));
    }
  }, [value, displayValue]);

  const handleChange = (raw: string) => {
    // Amounts may be negative (a surcharge); percentages may not — a negative
    // percentage is silently dropped by discountsDataToRequests (value <= 0),
    // so reject it at the input to keep preview and persistence in sync.
    const pattern = type === "percentage" ? /^\d*\.?\d*$/ : /^-?\d*\.?\d*$/;
    if (raw !== "" && !pattern.test(raw)) {
      return;
    }
    setDraft(raw);
    // A lone "-", "." or empty string is not yet a number; treat it as 0 in the
    // model while leaving the draft untouched so the user can keep typing.
    const parsed = parseFloat(raw);
    const numeric = Number.isNaN(parsed) ? 0 : parsed;
    const model = type === "percentage" ? numeric : chfToRappen(numeric);
    lastEmitted.current = model;
    onChange(model);
  };

  return (
    <div className="relative">
      <Input
        id={id}
        type="text"
        inputMode="decimal"
        value={draft}
        onChange={(e) => handleChange(e.target.value)}
        className={type === "percentage" ? "pr-8" : "pr-10"}
        placeholder="0"
      />
      <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-muted-foreground">
        {type === "percentage" ? "%" : currencyLabel}
      </span>
    </div>
  );
}

export function ContractWizardStepDiscounts({
  discounts,
  onDiscountsChange,
  servicePattern,
  tariffVersions,
  childId,
  services = [],
  subsidised,
  subsidyAmount,
  siblingDiscountManual,
  onSiblingDiscountManualChange,
  startDate,
  showHeader = true,
}: ContractWizardStepDiscountsProps) {
  const { i18n } = useLingui();
  const { currency } = useCurrency();
  const { options: groupTypeOptions = [] } = useEnumOptions("groups", "type");
  const { data: billingSettings } = useBillingSettings();
  const activeCurrency = currency || DEFAULT_CURRENCY;
  const formatAmount = useCallback(
    (valueInRappen: number) =>
      formatCHF(rappenToCHF(valueInRappen), true, activeCurrency),
    [activeCurrency]
  );

  // Calculate cost impact with enhanced calculation hook
  // Compute BASE amounts before discounts to avoid double-discounting in preview
  const costCalculationResult = useMultiGroupCostCalculation(
    servicePattern || {},
    tariffVersions || {},
    childId || "",
    services,
    undefined,
    {
      useEnhancedAPI: true,
      includeDiscounts: false,
      isSubsidised: !!subsidised,
      calculationDate: startDate,
      lockAgeToCalculationMonth: true,
    }
  );

  // Check which days have services scheduled
  const daysWithServices = useMemo(() => {
    if (!servicePattern) return new Set<number>();

    const dayNameToKey: Record<string, number> = {
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
      sunday: 7,
    };

    const days = new Set<number>();
    Object.values(servicePattern).forEach((groupPattern) => {
      Object.keys(groupPattern).forEach((dayName) => {
        const servicesForDay = groupPattern[dayName];
        if (servicesForDay && servicesForDay.length > 0) {
          const key = dayNameToKey[dayName.toLowerCase()];
          if (key) {
            days.add(key);
          }
        }
      });
    });
    return days;
  }, [servicePattern]);

  // Update weekday discount for a specific day
  const handleWeekdayDiscountChange = useCallback(
    (day: number, value: string) => {
      const numValue = parseFloat(value || "0");
      const rappenValue = chfToRappen(numValue);

      const newWeekdayDiscounts = {
        ...(discounts.weekday || {}),
        [day]: rappenValue,
      };

      // Remove the discount if it's 0
      if (rappenValue === 0) {
        delete newWeekdayDiscounts[day];
      }

      onDiscountsChange({
        ...discounts,
        weekday:
          Object.keys(newWeekdayDiscounts).length > 0
            ? newWeekdayDiscounts
            : undefined,
      });
    },
    [discounts, onDiscountsChange]
  );

  // Clear weekday discounts for days without services
  const clearInactiveDayDiscounts = useCallback(() => {
    if (!discounts.weekday) return;

    const updatedWeekday: WeekdayDiscount = {};
    Object.entries(discounts.weekday).forEach(([day, amount]) => {
      if (daysWithServices.has(parseInt(day))) {
        updatedWeekday[parseInt(day)] = amount;
      }
    });

    onDiscountsChange({
      ...discounts,
      weekday:
        Object.keys(updatedWeekday).length > 0 ? updatedWeekday : undefined,
    });
  }, [discounts, daysWithServices, onDiscountsChange]);

  // Custom discount handlers
  const addCustomDiscount = useCallback(() => {
    const newDiscount: CustomDiscount = {
      id: `discount-${Date.now()}`,
      name: "",
      type: "amount",
      value: 0,
    };

    onDiscountsChange({
      ...discounts,
      custom: [...(discounts.custom || []), newDiscount],
    });
  }, [discounts, onDiscountsChange]);

  const removeCustomDiscount = useCallback((index: number) => {
    const updatedCustom = [...(discounts.custom || [])];
    updatedCustom.splice(index, 1);

    onDiscountsChange({
      ...discounts,
      custom: updatedCustom.length > 0 ? updatedCustom : undefined,
    });
  }, [discounts, onDiscountsChange]);

  const updateCustomDiscount = useCallback(
    (index: number, updates: Partial<CustomDiscount>) => {
      const updatedCustom = [...(discounts.custom || [])];
      updatedCustom[index] = { ...updatedCustom[index], ...updates };

      onDiscountsChange({
        ...discounts,
        custom: updatedCustom,
      });
    },
    [discounts, onDiscountsChange]
  );


  // Map group type code -> human label (moved before early return)
  const groupTypeLabel = useMemo(() => {
    if (!servicePattern) return {};
    const map: Record<string, string> = {};
    (groupTypeOptions || []).forEach((opt) => {
      if (opt.value) map[opt.value] = opt.label || opt.value;
    });
    return map;
  }, [groupTypeOptions, servicePattern]);

  // Auto-clear when services change
  React.useEffect(() => {
    clearInactiveDayDiscounts();
  }, [daysWithServices]); // eslint-disable-line react-hooks/exhaustive-deps

  // Early return after all hooks
  if (
    !costCalculationResult ||
    !("totalCostBreakdown" in costCalculationResult)
  ) {
    // Handle the case where enhanced result is not available
    return null; // or show an appropriate message
  }

  // Extract group breakdowns to show per-day rates per group
  const groupCostBreakdowns =
    (costCalculationResult as EnhancedMultiGroupCostResult)
      .groupCostBreakdowns || {};

  const weekdayLabels = {
    1: i18n._(msg`Monday`),
    2: i18n._(msg`Tuesday`),
    3: i18n._(msg`Wednesday`),
    4: i18n._(msg`Thursday`),
    5: i18n._(msg`Friday`),
  };

  const dayNumToKey: Record<number, string> = {
    1: "monday",
    2: "tuesday",
    3: "wednesday",
    4: "thursday",
    5: "friday",
  };

  return (
    <div className="space-y-6">
      {showHeader && (
        <div>
          <h3 className="text-lg font-medium">
            <Trans id="contracts.wizard.discounts.title">
              Discount Configuration
            </Trans>
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            <Trans id="contracts.wizard.discounts.description">
              Configure weekly recurring discounts and custom discounts for this
              contract
            </Trans>
          </p>
        </div>
      )}

      {/* Sibling-discount manual override */}
      {onSiblingDiscountManualChange && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              <Trans id="contracts.discounts.siblingManual.title">
                Sibling discount
              </Trans>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-3">
              <Checkbox
                id="sibling-discount-manual"
                checked={!!siblingDiscountManual}
                onCheckedChange={(checked) =>
                  onSiblingDiscountManualChange(checked === true)
                }
              />
              <div className="space-y-1">
                <Label
                  htmlFor="sibling-discount-manual"
                  className="font-medium cursor-pointer"
                >
                  <Trans id="contracts.discounts.siblingManual.toggleLabel">
                    Manage sibling discount manually
                  </Trans>
                </Label>
                <p className="text-sm text-muted-foreground">
                  <Trans id="contracts.discounts.siblingManual.toggleHelp">
                    When active, the system does not auto-calculate a sibling
                    discount for this family. Enter a custom discount below
                    instead.
                  </Trans>
                </p>
                <p className="text-xs text-muted-foreground italic">
                  <Trans id="contracts.discounts.siblingManual.familyScopeHint">
                    This setting applies to the whole family: if set on one
                    child, no child in this family receives an automatic
                    sibling discount.
                  </Trans>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Weekday Discounts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            <Trans id="contracts.wizard.discounts.weekday.title">
              Weekday Discounts
            </Trans>
          </CardTitle>
          <CardDescription>
            <Trans id="contracts.wizard.discounts.weekday.description">
              Set fixed discount amounts for specific weekdays. These discounts
              apply every week.
            </Trans>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-3">
            {WEEKDAYS.map(({ key }) => {
              const hasServices = daysWithServices.has(key);
              const discountValue = discounts.weekday?.[key] || 0;

              return (
                <div key={key} className="flex flex-col h-full">
                  {/* Top: Weekday label */}
                  <div className="mb-0.5">
                    <Label
                      htmlFor={`weekday-${key}`}
                      className={!hasServices ? "text-muted-foreground" : ""}
                    >
                      {weekdayLabels[key as keyof typeof weekdayLabels]}
                    </Label>
                  </div>

                  {/* Middle: Services or "No services" */}
                  <div className="text-xs text-muted-foreground flex-1">
                    {hasServices ? (
                      <div className="space-y-0.5">
                        {Object.entries(groupCostBreakdowns)
                          .map(([groupType, breakdown]) => {
                            const dayKey = dayNumToKey[key];
                            const amount =
                              (breakdown?.daily as Record<string, { amount?: number }>)?.[dayKey]?.amount || 0;
                            if (!amount) return null;
                            const label =
                              groupTypeLabel[groupType] || groupType;
                            return (
                              <div key={`${groupType}-${key}`}>
                                {formatAmount(amount)} ({label})
                              </div>
                            );
                          })
                          .filter(Boolean)}
                      </div>
                    ) : (
                      <p>
                        <Trans id="contracts.wizard.discounts.weekday.noServices">
                          No services
                        </Trans>
                      </p>
                    )}
                  </div>

                  {/* Bottom: Input always bottom-aligned */}
                  <div className="relative mt-1">
                    <Input
                      id={`weekday-${key}`}
                      type="number"
                      min="0"
                      step="0.05"
                      value={rappenToCHF(discountValue)}
                      onChange={(e) =>
                        handleWeekdayDiscountChange(key, e.target.value)
                      }
                      disabled={!hasServices}
                      className="pr-10 text-base"
                      placeholder="0.00"
                      title={
                        !hasServices
                          ? i18n._(msg`No services scheduled for this day`)
                          : undefined
                      }
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                      {activeCurrency}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {daysWithServices.size === 0 && (
            <Alert className="mt-4">
              <Info className="h-4 w-4" />
              <AlertDescription>
                <Trans id="contracts.wizard.discounts.weekday.noSchedule">
                  No services have been scheduled yet. Configure the schedule
                  first to enable weekday discounts.
                </Trans>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Custom Discounts Card */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Calculator className="h-4 w-4" />
              <Trans id="contracts.wizard.discounts.custom.title">
                Custom Discounts
              </Trans>
            </CardTitle>
            <CardDescription>
              <Trans id="contracts.wizard.discounts.custom.description">
                Add one-time or special discounts with custom names and values.
              </Trans>
            </CardDescription>
          </div>
          <Button type="button" variant="default" onClick={addCustomDiscount}>
            <Plus className="h-4 w-4 mr-2" />
            <Trans id="common.add">Add</Trans>
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {discounts.custom?.map((discount, index) => (
            <div key={discount.id || index} className="flex gap-3 items-end">
              <div className="flex-1">
                <Label htmlFor={`custom-name-${index}`}>
                  <Trans id="contracts.wizard.discounts.custom.name">
                    Name
                  </Trans>
                </Label>
                <Input
                  id={`custom-name-${index}`}
                  type="text"
                  value={discount.name}
                  onChange={(e) =>
                    updateCustomDiscount(index, { name: e.target.value })
                  }
                />
              </div>

              <div className="w-32">
                <Label htmlFor={`custom-type-${index}`}>
                  <Trans id="contracts.wizard.discounts.custom.type">
                    Type
                  </Trans>
                </Label>
                <Select
                  value={discount.type}
                  onValueChange={(value: "percentage" | "amount") =>
                    updateCustomDiscount(index, {
                      type: value,
                      value: 0,
                    })
                  }
                >
                  <SelectTrigger id={`custom-type-${index}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="amount">
                      <Trans id="contracts.wizard.discounts.custom.type.amount">
                        Amount
                      </Trans>
                    </SelectItem>
                    <SelectItem value="percentage">
                      <Trans id="contracts.wizard.discounts.custom.type.percentage">
                        Percentage
                      </Trans>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="w-32">
                <Label htmlFor={`custom-value-${index}`}>
                  <Trans id="contracts.wizard.discounts.custom.value">
                    Value
                  </Trans>
                </Label>
                <CustomDiscountValueInput
                  id={`custom-value-${index}`}
                  type={discount.type}
                  value={discount.value}
                  currencyLabel={activeCurrency}
                  onChange={(value) =>
                    updateCustomDiscount(index, { value })
                  }
                />
              </div>

              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeCustomDiscount(index)}
                className="mb-0"
                aria-label={i18n._({ id: "contracts.wizard.discounts.custom.remove", message: "Remove discount" })}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Sibling Discount Card.
          `sibling_discount_2nd_child` / `_3rd_plus` are optional on
          `models_BillingSettings`: nil means "not configured", i.e. no sibling
          discount. `?? 0` is exactly that documented nil semantic and keeps the
          previous runtime behaviour (`undefined > 0` was already `false`) — it
          is NOT an invented numeric default. */}
      {billingSettings &&
        ((billingSettings.sibling_discount_2nd_child ?? 0) > 0 ||
          (billingSettings.sibling_discount_3rd_plus ?? 0) > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="h-4 w-4" />
                <Trans id="contracts.wizard.discounts.sibling.title">
                  Sibling Discount (Automatic)
                </Trans>
              </CardTitle>
              <CardDescription className="space-y-1">
                <span className="block">
                  <Trans id="contracts.wizard.discounts.sibling.info.line1">
                    Sibling discounts are applied automatically during invoice generation.
                  </Trans>
                </span>
                <span className="block">
                  <Trans id="contracts.wizard.discounts.sibling.info.line2">
                    The discount applies to the cheapest contracts, with the most expensive contract paying full price.
                  </Trans>
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-sm flex items-center flex-wrap gap-2">
                {(billingSettings.sibling_discount_2nd_child ?? 0) > 0 && (
                  <>
                    <span>
                      <Trans id="contracts.wizard.discounts.sibling.2ndChild">
                        2nd child
                      </Trans>
                      :
                    </span>
                    <Badge variant="outline">
                      {billingSettings.sibling_discount_2nd_child}%{" "}
                      <Trans id="contracts.wizard.discounts.sibling.off">
                        off
                      </Trans>{" "}
                      <span className="text-muted-foreground ml-1">
                        (
                        <Trans id="contracts.wizard.discounts.sibling.cheapestShort">
                          cheapest
                        </Trans>
                        )
                      </span>
                    </Badge>
                  </>
                )}
                {(billingSettings.sibling_discount_2nd_child ?? 0) > 0 &&
                  (billingSettings.sibling_discount_3rd_plus ?? 0) > 0 && (
                    <span className="text-muted-foreground">-</span>
                  )}
                {(billingSettings.sibling_discount_3rd_plus ?? 0) > 0 && (
                  <>
                    <span>
                      <Trans id="contracts.wizard.discounts.sibling.3rdChild">
                        3rd+ child
                      </Trans>
                      :
                    </span>
                    <Badge variant="outline">
                      {billingSettings.sibling_discount_3rd_plus}%{" "}
                      <Trans id="contracts.wizard.discounts.sibling.off">
                        off
                      </Trans>{" "}
                      <span className="text-muted-foreground ml-1">
                        (
                        <Trans id="contracts.wizard.discounts.sibling.2ndCheapest">
                          2nd cheapest
                        </Trans>
                        )
                      </span>
                    </Badge>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

      {/* Cost Impact Card - Display calculated discount impact */}
      {costCalculationResult.totalCostBreakdown &&
        costCalculationResult.totalCostBreakdown.totalAmount > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Calculator className="h-4 w-4" />
              <Trans id="contracts.wizard.discounts.costImpact">
                Cost Impact
              </Trans>
            </CardTitle>
            <CardDescription>
              <Trans id="contracts.wizard.discounts.costImpactDescription">
                Preview of discounts applied to your monthly cost
              </Trans>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Base Monthly Cost */}
            <div className="flex justify-between items-center">
              <span className="text-sm">
                <Trans id="contracts.wizard.discounts.baseMonthlyCost">
                  Base Monthly Cost:
                </Trans>
              </span>
              <span className="text-sm font-medium">
                {formatAmount(costCalculationResult.totalCostBreakdown.totalAmount)}
              </span>
            </div>

            {/* Subsidy Amount */}
            {subsidised && subsidyAmount && subsidyAmount > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm">
                  <Trans id="contracts.wizard.discounts.subsidyAmount">
                    Subsidy Amount:
                  </Trans>
                </span>
                <span className="text-sm font-medium text-success-on-surface">
                  -{formatAmount(subsidyAmount)}
                </span>
              </div>
            )}

            {/* Weekday Discounts */}
            {discounts.weekday && Object.keys(discounts.weekday).length > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm">
                  <Trans id="contracts.wizard.discounts.weekdayDiscounts">
                    Weekday Discounts:
                  </Trans>
                </span>
                <span className="text-sm font-medium text-success-on-surface">
                  -{formatAmount(
                    Object.values(discounts.weekday).reduce((sum, amount) => sum + amount, 0)
                  )}
                </span>
              </div>
            )}

            {/* Custom Discounts */}
            {discounts.custom && discounts.custom.length > 0 && (
              <div className="space-y-2">
                {discounts.custom.map((discount, idx) => {
                  const amount =
                    discount.type === "percentage"
                      ? Math.round(
                          (costCalculationResult.totalCostBreakdown.totalAmount *
                            discount.value) /
                            100
                        )
                      : discount.value;

                  // A negative custom amount is a surcharge: it ADDS to the
                  // total, so show it as a red "+" line instead of a green "-".
                  const isSurcharge = amount < 0;

                  return (
                    <div key={discount.id || idx} className="flex justify-between items-center">
                      <span className="text-sm">
                        {discount.name || i18n._(msg`Custom Discount ${idx + 1}`)}:
                      </span>
                      <span
                        className={`text-sm font-medium ${isSurcharge ? "text-destructive" : "text-success-on-surface"}`}
                      >
                        {isSurcharge ? "+" : "-"}
                        {formatAmount(Math.abs(amount))}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Divider */}
            <div className="border-t pt-3">
              {/* Estimated Monthly Total */}
              <div className="flex justify-between items-center">
                <span className="font-medium">
                  <Trans id="contracts.wizard.discounts.estimatedMonthlyTotal">
                    Estimated Monthly Total:
                  </Trans>
                </span>
                <span className="text-lg font-bold text-primary">
                  {(() => {
                    const baseAmount = costCalculationResult.totalCostBreakdown.totalAmount;
                    const subsidyAmt = (subsidised && subsidyAmount) ? subsidyAmount : 0;
                    const weekdayDiscount = discounts.weekday
                      ? Object.values(discounts.weekday).reduce((sum, amount) => sum + amount, 0)
                      : 0;
                    const customDiscount = discounts.custom
                      ? discounts.custom.reduce((sum, discount) => {
                          if (discount.type === "percentage") {
                            return (
                              sum +
                              Math.round((baseAmount * discount.value) / 100)
                            );
                          }
                          return sum + discount.value;
                        }, 0)
                      : 0;
                    const total = baseAmount - subsidyAmt - weekdayDiscount - customDiscount;
                    return formatAmount(Math.max(0, total));
                  })()}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
}
